// The card-formula damage table for one attacker against a team: one row per attack or ability (basic attack(s), Jump,
// then the damaging and status skills), one cell per defender, each with its number, the hit chance and the step-by-step
// modifier chain (abilityFormula's MOD 2 / MOD 5 pipeline). This was the Fight View's matrix (FightModal.vue, retired
// 2026-10-23); the spotlight's unit-vs-team table (UnitVsPanel) now uses it for the breakdown when you click a cell.
// Pure: game data is passed in.
import { unitCombatStats } from './unitStats.js';
import { unitElementProfile } from './elements.js';
import { zodiacCompat } from './zodiac.js';
import { computeAbilityDamage, computeSuccess, parseAbilityInfo, legacyAbilityFormula } from './abilityFormula.js';
import { weaponAttackModel, unarmedAttackModel, jumpAttackModel } from './damage.js';
import { hitChance } from './evasion.js';

// The pretend statuses a viewer can put on every target.
export const PRETEND_STATUSES = [
  ['protect', 'Protect'], ['shell', 'Shell'], ['charging', 'Charging'], ['sleeping', 'Sleeping'], ['frogChicken', 'Frog / Chicken'], ['oil', 'Oil'],
];

// attacker: the unit; team: its roster (a Mime copies its teammates' skills); foes: the other roster.
// gd: { charStats, itemschanged, notAbles, abilitiesInfo, monsterSkills }; statuses: { protect, shell, … } (all optional).
export function fightMatrix(attacker, team, foes, gd, statuses = {}) {
  const s = unitCombatStats(attacker, gd.charStats, gd.itemschanged, gd.notAbles);
  const profile = unitElementProfile(s.equipped);
  const u = attacker, eq = s.equipped;
  const attackerMods = {
    strengthen: profile.strengthen,
    attackUp: u.SupportSkill === 'Attack Up',
    magicAttackUp: u.SupportSkill === 'Magic Attack Up',
    martialArts: u.SupportSkill === 'Martial Arts' || u.Class === 'Monk', // ×3/2 on Punch Art
  };

  const defenders = foes.map((d) => {
    const ds = unitCombatStats(d, gd.charStats, gd.itemschanged, gd.notAbles);
    const dp = unitElementProfile(ds.equipped);
    const zodiac = zodiacCompat(u.Gender, u.Sign, d.Gender, d.Sign);
    return {
      unit: d, stats: ds.stats, profile: dp, equipped: ds.equipped, cEv: ds.classBase.cEvPercent, reaction: d.ReactionSkill, zodiac,
      mods: {
        defenseUp: d.SupportSkill === 'Defense Up', magicDefendUp: d.SupportSkill === 'Magic Defense Up',
        protect: !!statuses.protect, shell: !!statuses.shell, charging: !!statuses.charging, sleeping: !!statuses.sleeping, frogChicken: !!statuses.frogChicken,
        zodiac, absorb: dp.absorb, half: dp.half, weak: new Set([...dp.weak, ...(statuses.oil ? ['Fire'] : [])]),
      },
    };
  });

  // Rows: the basic attack(s) (one per weapon with Dual Wield; Doublehand's ×2 is in weaponAttackModel), Jump, then
  // the damaging and status skills.
  const rows = [];
  const wMain = eq.Mainhand && eq.Mainhand.wp ? weaponAttackModel(eq.Mainhand, u) : null;
  const wOff = u.SupportSkill === 'Dual Wield' && eq.Offhand && eq.Offhand.wp ? weaponAttackModel(eq.Offhand, u) : null;
  if (wMain) rows.push({ label: `Attack: ${u.Mainhand}`, attack: true, model: wMain, element: wMain.element, evadable: eq.Mainhand.type !== 'Gun' });
  if (wOff) rows.push({ label: `Attack: ${u.Offhand}`, attack: true, model: wOff, element: wOff.element, evadable: eq.Offhand.type !== 'Gun' });
  if (!wMain && !wOff) rows.push({ label: 'Attack: Unarmed', attack: true, model: unarmedAttackModel(u), element: null, evadable: true });
  const skills = [...(u.ClassSkills || []), ...(u.ExtraSkills || [])];
  if (skills.some((n) => /^(Level|Vertical) Jump/.test(n || ''))) rows.push({ label: 'Jump', model: jumpAttackModel(eq.Mainhand, u), element: null, attackerMods: {}, evadable: false });

  const legacyCtx = { pa: s.stats.pa, ma: s.stats.ma, brave: u.Brave, faith: u.Faith, wp: eq.Mainhand.wp, weaponType: eq.Mainhand.type, paUnarmed: s.paUnarmed };
  const seen = new Set();
  for (const su of u.Class === 'Mime' ? team : [u]) {
    const names = [...(su.ClassSkills || []), ...(su.ExtraSkills || []), ...((gd.monsterSkills && gd.monsterSkills[su.Class]) || [])].map((n) => (n || '').replace('*', ''));
    for (const name of names) {
      if (seen.has(name)) continue;
      seen.add(name);
      const model = parseAbilityInfo((gd.abilitiesInfo || {})[name]);
      if (!model) {
        // a parser miss falls back to the slim legacy formulas (flat damage only; Jump has its own row)
        if (/^(Level|Vertical) Jump/.test(name)) continue;
        const legacy = legacyAbilityFormula(name, legacyCtx);
        if (legacy && legacy.kind === 'damage') rows.push({ label: name, flat: legacy.value, element: null, evadable: true });
        continue;
      }
      if (model.kind === 'number' && model.verb === 'Damage') rows.push({ label: name, model, element: model.element, evadable: true });
      else if (model.kind === 'percent') rows.push({ label: name, model, element: model.element, status: true });
    }
  }

  const ctxBase = { ma: s.stats.ma, pa: s.stats.pa, speed: s.stats.speed, brave: u.Brave, faith: u.Faith, wp: eq.Mainhand.wp, maxHp: s.stats.hp, paUnarmed: s.paUnarmed };
  const grid = rows.map((row) => {
    const isPercent = row.model && row.model.kind === 'percent';
    const aMods = row.attackerMods !== undefined ? row.attackerMods : attackerMods;
    return {
      ability: row.label, attack: !!row.attack, element: row.element, status: !!row.status,
      cells: defenders.map((d) => {
        const hitsOf = (type) => { const hc = (dir) => Math.round(hitChance(d, { type, direction: dir })); return { front: hc('front'), side: hc('side'), back: hc('back'), magical: type === 'magical' }; };
        if (row.flat != null) { const hitDirs = hitsOf('physical'); return { kind: 'damage', display: `${row.flat}`, steps: [], effect: null, hit: hitDirs.front, hitDirs }; }
        // targetMa / targetEquipped feed Unyielding Cut's branch (PA*(WP+TargetMA) on a target with no non-shield gear)
        const ctx = { ...ctxBase, targetFaith: d.unit.Faith, targetMa: d.stats.ma,
          targetEquipped: !!(d.unit.Mainhand || d.unit.Head || d.unit.Armor || d.unit.Accessory || (d.unit.Offhand && d.equipped.Offhand.type !== 'Shield')) };
        if (isPercent) { const { final, steps } = computeSuccess(row.model, ctx, { ...aMods, ...d.mods }); return { kind: 'percent', display: `${final}%`, steps, effect: null }; }
        const { final, steps } = computeAbilityDamage(row.model, ctx, { ...aMods, ...d.mods });
        let effect = null;
        if (row.element) {
          if (d.profile.absorb.has(row.element)) effect = 'absorb';
          else if (d.mods.weak.has(row.element)) effect = 'weak';
          else if (d.profile.half.has(row.element)) effect = 'half';
        }
        let hit = null, hitDirs = null;
        if (row.evadable) { hitDirs = hitsOf(row.model.magical ? 'magical' : 'physical'); hit = hitDirs.front; }
        return { kind: 'damage', display: Array.isArray(final) ? `${final[0]} - ${final[1]}` : `${final}`, steps, effect, hit, hitDirs };
      }),
    };
  });
  return { rows: grid, defenders: defenders.map((d) => ({ name: d.unit.Name, zodiac: d.zodiac })) };
}

// The matrix row for a sim action, by name: the sim's basic attack is "Attack", the matrix's "Attack: <weapon>".
export function matrixRowFor(matrix, actionName) {
  if (!matrix) return null;
  if (/^Attack\b/.test(actionName || '')) return matrix.rows.find((r) => r.attack) || null;
  return matrix.rows.find((r) => r.ability === actionName) || null;
}
